import {
  FC,
  FunctionComponent,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';
import { Box } from '@apia/theme';

interface IMakeResponsiveComponent<P extends Record<string, unknown>> {
  breakPoints: number[];
  Component: FunctionComponent<
    P & { breakPoint: number; breakPointWidth: number; containerWidth: number }
  >;
  /**
   * Este HOC hace un setState cada vez que cambia el ancho del contenedor. Hay
   * situaciones en las que es deseable agregar un debounce a este
   * comportamiento para mejorar la performance. *Por defecto usa 100*
   */
  debounce?: number;
  /**
   * Por defecto usa 4.
   */
  defaultBreakpoint?: number;
}

function calcBreakPoint(currentWidth: number, breakPoints: number[]) {
  let currentIndex = 0;

  while (
    currentIndex < breakPoints.length &&
    currentWidth > breakPoints[currentIndex]
  )
    currentIndex++;

  return currentIndex;
}

export interface IResponsiveComponent<P> extends FC<P> {
  setBreakpoints: { current: (breakPoints: number[]) => void };
}

/**
 * El método makeResponsiveComponent agrega un wrapper alrededor de un
 * componente para conocer el ancho del contenedor del mismo. De esta forma, es
 * fácil predecir qué ancho dispone el mismo para renderizarse y de esa forma
 * poder tomar decisiones de responsividad que no dependen del ancho de la
 * pantalla sino del espacio donde se encuentra.
 */
export const makeResponsiveComponent = <P extends Record<string, unknown>>({
  breakPoints,
  Component,
  defaultBreakpoint,
}: IMakeResponsiveComponent<P>): IResponsiveComponent<
  P & {
    breakPoints?: number[];
  }
> => {
  const setBreakpoints: { current: (b: number[]) => void } = {
    current: () => {},
  };

  return Object.assign(
    function ResponsiveComponent(
      props: P & {
        breakPoints?: number[];
      },
    ) {
      const [breakPoint, setCurrentBreakpoint] = useState<number>(
        defaultBreakpoint ?? 4,
      );

      const [innerBreakpoints, setInnerBreakpoints] = useState(
        props.breakPoints ?? breakPoints,
      );
      setBreakpoints.current = setInnerBreakpoints;

      const [containerWidth, setContainerWidth] = useState(
        breakPoints[defaultBreakpoint ?? 4],
      );
      const observer = useRef<ResizeObserver | null>(null);
      const shouldDisconnect = useRef(false);

      const [hasRendered, setHasRendered] = useState(false);
      const [, startTransition] = useTransition();

      const ref = useCallback(
        (element: HTMLElement) => {
          if (!element) return;

          observer.current?.disconnect();
          observer.current = new ResizeObserver((resizeProps) => {
            if (resizeProps[0].contentRect.width === 0) return;

            startTransition(() => {
              setContainerWidth(resizeProps[0].contentRect.width);
              setCurrentBreakpoint(
                calcBreakPoint(
                  resizeProps[0].contentRect.width,
                  innerBreakpoints,
                ),
              );
              setHasRendered(true);
            });
          });

          if (element) observer.current.observe(element);
        },
        [innerBreakpoints],
      );

      useEffect(() => {
        shouldDisconnect.current = false;

        return () => {
          shouldDisconnect.current = true;
          setTimeout(() => {
            if (shouldDisconnect.current) {
              observer.current?.disconnect();
            }
            /**
             * El timeout tiene un tiempo arbitrariamente alto ya que de esta
             * forma el observer se va a desconectar una vez que el componente
             * se desmonte. Si pusiera un tiempo menor, caería en el riesgo de
             * que se desconecte aún cuando el componente está montado
             */
          }, 2000);
        };
      }, [innerBreakpoints]);

      const deferBreakPoint = useDeferredValue(breakPoint);
      const deferContainerWidth = useDeferredValue(containerWidth);

      return (
        <Box ref={ref}>
          {hasRendered ? (
            <Component
              {...props}
              breakPoint={deferBreakPoint}
              breakPointWidth={breakPoints[deferBreakPoint]}
              containerWidth={deferContainerWidth}
            />
          ) : null}
        </Box>
      );
    },
    { setBreakpoints },
  );
};
